import type { AdminUserRef, HypothesisTransition } from '../api/useHypoteseBehandling.ts';
import type { Hypothesis, HypothesisConfig, HypothesisSummary } from '../api/useHypotheses.ts';
import { hypoteseStatusNoekkel } from '../status/status.ts';
import { kortDato, kortDatoAv } from './setning.ts';

/**
 * Det behandleren og beslutningstakeren leser av en hypotese (H2.3): navnene, datoene,
 * rollene på hypotesen og neste steg. Rene funksjoner, så reglene testes uten et tre rundt seg.
 * Tiltalen er «du» i hele rommet (DD-60, P4): den innloggede er «du», «deg» og «din».
 */

export const DAG_MS = 86_400_000;

/**
 * En adminbruker uten visningsnavn er «en kollega» overalt (H2.5-funn 1, produkteier 2026-10-28):
 * «En kollega» i setningsstart (`stor`), «en kollegas» i genitiv — aldri «en», «ens» eller «—».
 */
const UTEN_NAVN = 'en kollega';

/** Visningsnavnet — aldri e-post (AdminUserRef). Mangler det, står en nøytral omtale. */
export const navnAv = (ref: AdminUserRef | undefined): string => ref?.displayName?.trim() || UTEN_NAVN;

/** Fornavnet der setningen snakker om personen («Jonas får spørsmålet»). Uten navn hele omtalen. */
export const fornavn = (ref: AdminUserRef | undefined): string => (ref?.displayName?.trim() ? (navnAv(ref).split(' ')[0] ?? navnAv(ref)) : UTEN_NAVN);

/** Stor forbokstav der et navn begynner en setning: «En kollega får beskjed.» Et navn er uendret. */
export const stor = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

/** «du»/«deg»/«Du» når personen er den innloggede, ellers navnet — «Du» er setningsstart. */
export function hvem(ref: AdminUserRef | undefined, megId: string | undefined, form: 'du' | 'Du' | 'deg'): string {
  if (ref && megId && ref.id === megId) return form;
  return form === 'Du' ? stor(navnAv(ref)) : navnAv(ref);
}

/**
 * Leietakerens mal med plassholderne fylt ut. En verdi i setningsstart får stor forbokstav;
 * en plassholder uten verdi ennå vises som en tankestrek («Hypotesekontrakten før H1.4»).
 */
export function fyllMal(mal: string, verdier: Record<string, string | undefined>): string {
  return mal.replace(/\{(\w+)\}/g, (_, k: string, pos: number) => {
    const v = verdier[k];
    if (v === undefined) return '—';
    return pos === 0 || /[.!?]\s+$/.test(mal.slice(0, pos)) ? stor(v) : v;
  });
}

/** Genitiv på norsk: «Rune Aas’», «Kari Lunds». */
export const genitiv = (navn: string): string => (/[sxz]$/i.test(navn) ? `${navn}’` : `${navn}s`);

/** «A», «A og B», «A, B og C». */
export function oppramsing(deler: string[]): string {
  if (deler.length <= 1) return deler[0] ?? '';
  return `${deler.slice(0, -1).join(', ')} og ${deler[deler.length - 1]}`;
}

/** Leietakerens navn på statusen (`HypothesisStatusWording.label`). */
export function statusNavn(config: HypothesisConfig | undefined, h: Pick<HypothesisSummary, 'status' | 'outcome'>): string {
  const noekkel = hypoteseStatusNoekkel(h.status, h.outcome);
  return config?.statusWording.find((w) => w.key === noekkel)?.label ?? '';
}

/** I dag som «ÅÅÅÅ-MM-DD» i Europe/Oslo — grensene for datoene arkene tar. */
export function isoDag(d: Date): string {
  const deler = new Intl.DateTimeFormat('nb-NO', { timeZone: 'Europe/Oslo', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const hent = (t: string) => deler.find((p) => p.type === t)?.value ?? '';
  return `${hent('year')}-${hent('month')}-${hent('day')}`;
}

export const leggTilDager = (iso: string, dager: number): string => isoDag(new Date(new Date(`${iso}T12:00:00Z`).getTime() + dager * DAG_MS));

export function leggTilMaaneder(iso: string, maaneder: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dato = new Date(Date.UTC(y!, m! - 1 + maaneder, d!, 12));
  return dato.toISOString().slice(0, 10);
}

/** «ÅÅÅÅ-MM-DD» → «4. jan 2027» når året ikke er i år; ellers «4. jan». */
export function datoMedAar(iso: string, iDag: string): string {
  return iso.slice(0, 4) === iDag.slice(0, 4) ? kortDato(iso) : `${kortDato(iso)} ${iso.slice(0, 4)}`;
}

/** Varselet på et utkast står de siste dagene før slettedatoen (domenemodellens retensjonstabell). */
export const SLETTEVARSEL_DAGER = 14;

/**
 * «Slettes [dato]» på et utkast: tjenerens `draftDeletesOn` er innen {@link SLETTEVARSEL_DAGER}
 * dager fra i dag (`iDag` som «ÅÅÅÅ-MM-DD»). Datoene er `plainDate`, så de sammenlignes som tekst.
 */
export function slettesSnart(draftDeletesOn: string | undefined, iDag: string): boolean {
  return draftDeletesOn !== undefined && draftDeletesOn <= leggTilDager(iDag, SLETTEVARSEL_DAGER);
}

/** Et tidspunkt klokka 12 lokal tid den dagen — «innen 25. okt» betyr hele dagen. */
export function tidspunktFor(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, m! - 1, d!, 12, 0, 0, 0).toISOString();
}

/** Hele dager fra i dag til tidspunktet, i Oslo-datoer. Negativt når det er passert. */
export function dagerTil(tidspunkt: string, naa = new Date()): number {
  const fra = new Date(`${isoDag(naa)}T12:00:00Z`).getTime();
  const til = new Date(`${isoDag(new Date(tidspunkt))}T12:00:00Z`).getTime();
  return Math.round((til - fra) / DAG_MS);
}

/**
 * Rollene den innloggede har på én hypotese. `beslutter` er lista (`config.deciders`) — den
 * som står der, kan velge og avvise, men aldri sin egen innsending (422
 * `decider_is_submitter`). Rom-rollen ligger over: `behandler` kan ta til vurdering, `leserPluss`
 * ser notatene. `kanAvvise` er beslutningstakeren, og vurdereren når leietakeren har skrudd på
 * `reviewerMayReject` (config, 2026-09-27) — aldri innsenderen.
 */
export interface Roller {
  megId: string | undefined;
  innsender: boolean;
  vurderer: boolean;
  beslutter: boolean;
  kanAvvise: boolean;
  behandler: boolean;
  leserPluss: boolean;
  /** Hypotesens beslutningstaker (`decider`) — satt ved «Velg», ikke lista. Etter valgt er det denne som teller. */
  avgjoer: boolean;
  /** Hypotesens eier (`owner`). */
  eier: boolean;
}

export function rollerPaa(h: HypothesisSummary, megId: string | undefined, config: HypothesisConfig | undefined, rom: { behandler: boolean; leserPluss: boolean }): Roller {
  const innsender = megId !== undefined && h.submittedBy.id === megId;
  const vurderer = megId !== undefined && h.reviewer?.id === megId;
  const beslutter = megId !== undefined && !innsender && Boolean(config?.deciders.some((d) => d.id === megId));
  return {
    megId,
    innsender,
    vurderer,
    beslutter,
    kanAvvise: beslutter || (vurderer && !innsender && config?.reviewerMayReject === true),
    behandler: rom.behandler,
    leserPluss: rom.leserPluss,
    avgjoer: megId !== undefined && h.decider?.id === megId,
    eier: megId !== undefined && h.owner?.id === megId,
  };
}

/**
 * Et neste steg som fylt knapp i statusens flate (DD-56). `flate` er statusen det gir; et steg
 * som ikke er en overgang («Gi videre …», «Løsne låsen …», «Juster før start …», «Send tilbake
 * med merknad …»), har ingen flate og ingen S-tast (DD-11 presisert 2026-09-26).
 */
export type HandlingK =
  | 'tm'
  | 'park'
  | 'velg'
  | 'avvis'
  | 'unpark'
  | 'godkjenn'
  | 'avslaa'
  | 'losne'
  | 'giVidere'
  | 'juster'
  | 'start'
  | 'konkluder'
  | 'kilde'
  | 'maaling'
  | 'pause'
  | 'fortsett'
  | 'stopp'
  | 'bekreft'
  | 'tilbake';
export interface Handling {
  k: HandlingK;
  navn: string;
  /** Bokstaven etter `s` (DD-11 presisert 2026-09-26): S T, S P, S V, S A, S O, S F, S S, S B. */
  tast?: string;
  flate?: string;
  /** Ikonet når steget ikke er en overgang (ingen flate å låne det fra). */
  ikon?: string;
}

export const MAKS_TRENGER_MER = 3;

type Rev = Hypothesis['revisions'][number];

/** Justeringen som venter på beslutningstakeren: siste revisjon, `justering`, verken godkjent eller avslått. */
export function ventendeJustering(h: Hypothesis): Rev | undefined {
  const siste = h.revisions[h.revisions.length - 1];
  return siste && siste.kind === 'justering' && !siste.approvedBy && !siste.declinedBy ? siste : undefined;
}

/** Revisjonen før `rev` som ikke er avslått — den som gjelder hvis `rev` avslås. */
export function revisjonenFoer(h: Hypothesis, rev: Rev): Rev | undefined {
  return [...h.revisions].reverse().find((r) => r.revision < rev.revision && !r.declinedBy);
}

const LEDD: Array<[keyof Rev['form'], string]> = [
  ['title', 'Tittelen'],
  ['change', 'Endringen'],
  ['audience', 'For hvem'],
  ['effect', 'Effekten'],
  ['metricName', 'Måltallet'],
  ['sourceText', 'Kilden'],
  ['direction', 'Retningen'],
  ['threshold', 'Terskelen'],
  ['unit', 'Enheten'],
  ['testEndsOn', 'Innen'],
];

/** Leddene som er endret fra én form til en annen, som [ledd, før, etter] — vist med strek og ramme, aldri farge alene. */
export function endredeLedd(fra: Partial<Rev['form']>, til: Partial<Rev['form']>): Array<[string, string, string]> {
  const ord = (v: unknown) => (v === undefined || v === null ? '—' : typeof v === 'number' ? String(v).replace('.', ',') : String(v));
  return LEDD.filter(([k]) => ord(fra[k]) !== ord(til[k])).map(([k, navn]) => [navn, ord(fra[k]), ord(til[k])]);
}

/**
 * Det som faktisk endret seg mellom to verdier, ord for ord: felles begynnelse og slutt skrelles
 * av («Fortsett å se» øverst på mobil → … i iOS-appen gir «på mobil» og «i iOS-appen»). Er den ene
 * siden tom (et rent tillegg eller en ren sletting), står hele verdiene — ellers mister leseren
 * sammenhengen. `foer`/`etter` er det som er felles, så setningen kan vise endringen på plass.
 */
export function endretDel(fra: string, til: string): { foer: string; ut: string; inn: string; etter: string } {
  const a = fra.split(' ');
  const b = til.split(' ');
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  let j = 0;
  while (j < a.length - i && j < b.length - i && a[a.length - 1 - j] === b[b.length - 1 - j]) j++;
  const ut = a.slice(i, a.length - j).join(' ');
  const inn = b.slice(i, b.length - j).join(' ');
  if (!ut || !inn) return { foer: '', ut: fra, inn: til, etter: '' };
  return { foer: a.slice(0, i).join(' '), ut, inn, etter: a.slice(a.length - j).join(' ') };
}

/** «» rundt en verdi — aldri rundt en som alt har «» i seg (da står den som den er). */
export const sitat = (v: string): string => (/[«»]/.test(v) ? v : `«${v}»`);

/** «på mobil» → «i iOS-appen» — én del per endret ledd, til arkene og loggen (P6b). */
export const endringTekst = (endret: Array<[string, string, string]>): string =>
  endret
    .map(([, f, t]) => {
      const d = endretDel(f, t);
      return `${sitat(d.ut)} → ${sitat(d.inn)}`;
    })
    .join('; ');

/** Om en lås er løsnet: en overstyring av den typen står i tidslinjen. */
export const overstyring = (h: Hypothesis, kind: 'baseline_mangler' | 'uavklart_kilde') => h.transitions.find((t) => t.override === kind);

/** Baseline er en lås før start når den ikke er målt, eller vurdereren sa «må måles» (409 baseline_missing). */
export function baselineLaast(h: Hypothesis): boolean {
  const sjekk = h.transitions.find((t) => t.reviewCheck)?.reviewCheck;
  return (h.baselineState !== 'maalt' || sjekk?.baselineCheck === 'maa_maales') && !overstyring(h, 'baseline_mangler');
}

/** Låsene beslutningstakeren kan løsne før start (override). Kilden `maa_bygges` er aldri her. */
export function loesbareLaaser(h: Hypothesis): Array<{ kind: 'baseline_mangler' | 'uavklart_kilde'; navn: string }> {
  const ut: Array<{ kind: 'baseline_mangler' | 'uavklart_kilde'; navn: string }> = [];
  if (h.status !== 'valgt') return ut;
  if (h.sourceStatus === 'uavklart_definisjon' && !overstyring(h, 'uavklart_kilde')) ut.push({ kind: 'uavklart_kilde', navn: 'kilden er uavklart' });
  if (baselineLaast(h)) ut.push({ kind: 'baseline_mangler', navn: 'baseline er ikke målt' });
  return ut;
}

/**
 * Eieren er deaktivert: hun står ikke blant rommets aktive personer (`config.members` har aldri
 * deaktiverte). Kontrakten har ikke et eget flagg; dette er lesningen av lista.
 */
export function eierDeaktivert(h: HypothesisSummary, config: HypothesisConfig | undefined): boolean {
  return Boolean(h.owner && config && config.members.length > 0 && !config.members.some((m) => m.id === h.owner!.id));
}

/** Konklusjonen er skrevet og ikke bekreftet (testes): beslutningstakerens tur, med mindre den er sendt tilbake. */
export function konklusjonVenter(h: Hypothesis): boolean {
  return h.status === 'testes' && h.conclusion?.kind === 'konklusjon' && !h.conclusion.frozenAt;
}

/** Sendt tilbake og ikke skrevet på nytt: merknaden står til eieren har skrevet (`sentBackAt` etter `writtenAt`). */
export function sendtTilbake(h: Hypothesis): boolean {
  const k = h.conclusion;
  return Boolean(k?.sentBackAt && k.sentBackAt >= k.writtenAt);
}

/** Statusen pausen kom fra — den `resume` går tilbake til. */
export function foerPausen(h: Hypothesis): 'valgt' | 'testes' {
  const t = [...h.transitions].reverse().find((x) => x.toStatus === 'pa_pause');
  return t?.fromStatus === 'valgt' ? 'valgt' : 'testes';
}

/**
 * Neste steg er overgangene tilstandsmaskinen tillater OG den innloggede kan gjøre (DD-56).
 * «Trenger mer» og «Kort fristen …» hører til blokken uten å være overganger. Velg finnes bare
 * fra Vurderes; fra Parkert finnes verken Velg eller Avvis — et menneske tar den tilbake.
 * Avvis er `kanAvvise`: beslutningstakeren, og vurdereren når `config.reviewerMayReject`.
 *
 * Etter valgt er hypotesens beslutningstaker (`avgjoer`) den som godkjenner og avslår justeringen,
 * løsner en lås, gir videre, stopper og bekrefter konklusjonen; eieren og beslutningstakeren
 * setter på pause og fortsetter; eieren og vurdereren justerer; vurdereren verifiserer kilden;
 * «Målingen er klar» er den som bygger den, vurdereren eller beslutningstakeren (kontrakten).
 * Funnene og konklusjonsskjemaet er eierens side (H2.2), ikke her; starten er en lenke dit
 * («Fyll ut oppstarten», oppstartskortet på /h/{nr}/start).
 */
export function handlingerFor(h: Hypothesis, r: Roller): Handling[] {
  const ut: Handling[] = [];
  const PAUSE: Handling = { k: 'pause', navn: 'Sett på pause …', tast: 'P', flate: 'pa_pause' };
  const STOPP: Handling = { k: 'stopp', navn: 'Stopp testen …', tast: 'S', flate: 'stoppet' };
  const GI_VIDERE: Handling = { k: 'giVidere', navn: 'Gi videre …', ikon: 'swap-horizontal-outline' };
  if (h.status === 'mottatt') {
    if (r.kanAvvise) ut.push({ k: 'avvis', navn: 'Avvis', tast: 'A', flate: 'avvist' });
  } else if (h.status === 'vurderes') {
    if (r.vurderer && r.behandler && h.needsMoreCount < MAKS_TRENGER_MER) ut.push({ k: 'tm', navn: 'Trenger mer', tast: 'T', flate: 'vurderes' });
    if (r.beslutter) ut.push({ k: 'velg', navn: 'Velg', tast: 'V', flate: 'valgt' });
    if ((r.vurderer && r.behandler) || r.beslutter) ut.push({ k: 'park', navn: 'Parker til …', tast: 'P', flate: 'parkert' });
    if (r.kanAvvise) ut.push({ k: 'avvis', navn: 'Avvis', tast: 'A', flate: 'avvist' });
  } else if (h.status === 'parkert') {
    if (r.behandler) ut.push({ k: 'unpark', navn: 'Ta tilbake til vurdering', tast: 'O', flate: 'vurderes' });
  } else if (h.status === 'valgt') {
    const venter = ventendeJustering(h);
    if (r.avgjoer && venter) {
      ut.push({ k: 'godkjenn', navn: 'Godkjenn justeringen …', flate: 'valgt' });
      ut.push({ k: 'avslaa', navn: 'Avslå justeringen …', flate: 'valgt' });
    }
    if (r.avgjoer && loesbareLaaser(h).length > 0) ut.push({ k: 'losne', navn: 'Løsne låsen …', ikon: 'key-outline' });
    // Oppstartskortet (W10) er eierens egen side, /h/{nr}/start — en lenke, ingen overgang her.
    // Feltene settes først ved start, så før det står «Fyll ut oppstarten».
    if (r.eier && h.ownerAcceptedAt && !h.startedAt) ut.push({ k: 'start', navn: h.notDoing && h.testStartsOn ? 'Start testen …' : 'Fyll ut oppstarten', ikon: 'play-forward-outline' });
    if ((r.eier || r.vurderer) && !venter && !h.startedAt) ut.push({ k: 'juster', navn: 'Juster før start …', ikon: 'create-outline' });
    if (r.vurderer && h.sourceStatus === 'uavklart_definisjon' && !overstyring(h, 'uavklart_kilde')) ut.push({ k: 'kilde', navn: 'Kilden er verifisert', ikon: 'checkmark-outline' });
    if (h.sourceStatus === 'maa_bygges' && (r.vurderer || r.avgjoer || (r.megId !== undefined && h.measurementOwner?.id === r.megId))) ut.push({ k: 'maaling', navn: 'Målingen er klar', ikon: 'construct-outline' });
    if (r.avgjoer) ut.push(GI_VIDERE);
    if (r.avgjoer || r.eier) ut.push(PAUSE);
    if (r.avgjoer) ut.push(STOPP);
  } else if (h.status === 'testes') {
    // Konklusjonen (W11) er eierens egen side, /h/{nr}/konklusjon — en lenke, ingen overgang her.
    // Sendt tilbake: eieren skriver den på nytt; den som var, står i feltene.
    if (r.eier && (!konklusjonVenter(h) || sendtTilbake(h))) ut.push({ k: 'konkluder', navn: sendtTilbake(h) ? 'Skriv konklusjonen på nytt' : 'Skriv konklusjonen', ikon: 'document-text-outline' });
    if (konklusjonVenter(h)) {
      if (r.avgjoer && !sendtTilbake(h)) {
        ut.push({ k: 'bekreft', navn: 'Bekreft konklusjonen', tast: 'B', flate: h.conclusion?.outcome ?? 'testes' });
        ut.push({ k: 'tilbake', navn: 'Send tilbake med merknad …', ikon: 'return-down-forward-outline' });
      }
    } else {
      if (r.avgjoer || r.eier) ut.push(PAUSE);
      if (r.avgjoer) ut.push(STOPP, GI_VIDERE);
    }
  } else if (h.status === 'pa_pause') {
    if (r.avgjoer || r.eier) ut.push({ k: 'fortsett', navn: 'Fortsett', tast: 'F', flate: foerPausen(h) });
    if (r.avgjoer) ut.push(STOPP, GI_VIDERE);
  }
  return ut;
}

/** Den ENE setningen om det andre har (DD-56) — aldri en knapp for en handling som ikke er din. */
export function andreSetning(h: Hypothesis, r: Roller, config: HypothesisConfig | undefined, handlinger: Handling[]): string | null {
  const beslutterne = (config?.deciders ?? []).filter((d) => d.id !== r.megId);
  const beslutterOrd = beslutterne.length > 0 ? oppramsing(beslutterne.map((d) => genitiv(navnAv(d)))) : null;
  const innsender = navnAv(h.submittedBy);
  if (h.status === 'mottatt') {
    if (r.innsender) return 'Du sendte den inn selv. En annen behandler tar den til vurdering.';
    if (handlinger.some((x) => x.k === 'avvis')) return 'Velg kommer når en vurderer har tatt den.';
    return null;
  }
  if (h.status === 'vurderes') {
    const vurderer = hvem(h.reviewer, r.megId, 'du');
    if (r.innsender) return `Du sendte den inn selv. ${vurderer === 'du' ? 'Du' : stor(vurderer)} vurderer den, og en beslutningstaker avgjør.`;
    if (r.vurderer && !r.beslutter) {
      if (!beslutterOrd) return 'Ingen står på lista over beslutningstakere ennå — eieren hos dere setter den opp.';
      return `${r.kanAvvise ? 'Velg' : 'Velg og Avvis'} er ${beslutterOrd} — de ser den under «Til deg» så snart den ikke venter på ${innsender}.`;
    }
    if (r.beslutter && !r.vurderer) return `«Trenger mer» og korting av fristen er ${genitiv(navnAv(h.reviewer))}, som vurderer den.`;
    if (r.beslutter && r.vurderer) return null;
    return `${stor(navnAv(h.reviewer))} vurderer den${beslutterOrd ? `; Velg og Avvis er ${beslutterOrd}` : ''}.`;
  }
  if (h.status === 'parkert') {
    if (handlinger.length === 0) return 'En behandler tar den tilbake til vurdering.';
    return h.parkCount >= 2 && r.beslutter ? 'Parkert to ganger. Er svaret nei, kan du avvise den når den er tilbake i Vurderes.' : 'Velg og Avvis kommer når den er tilbake i Vurderes.';
  }
  const eierF = fornavn(h.owner);
  const beslF = fornavn(h.decider);
  if (h.status === 'valgt') {
    if (r.avgjoer) {
      if (h.sourceStatus === 'maa_bygges') return `Kilden må bygges før testen kan starte — den låsen kan ingen løsne. Starten er ${genitiv(eierF)}.`;
      return `Starten og «Det vi ikke gjør» er ${genitiv(eierF)}; «Juster før start» er ${genitiv(eierF)} eller ${genitiv(fornavn(h.reviewer))}.`;
    }
    if (r.eier) return `Starten og «Det vi ikke gjør» skriver du på din side. «Gi videre» og stopp er ${genitiv(navnAv(h.decider))}.`;
    if (r.vurderer) return `Starten er ${genitiv(eierF)}; pause er ${genitiv(eierF)} eller ${genitiv(beslF)}; «Gi videre» og stopp er ${genitiv(beslF)}.`;
    return `${stor(navnAv(h.owner))} starter testen; ${navnAv(h.decider)} avgjør.`;
  }
  if (h.status === 'testes' && konklusjonVenter(h)) {
    if (sendtTilbake(h)) return `${r.avgjoer ? 'Du' : stor(navnAv(h.decider))} sendte konklusjonen tilbake; ${eierF} skriver på nytt.`;
    if (r.avgjoer) return null;
    return `${stor(navnAv(h.decider))} bekrefter eller sender den tilbake til ${eierF}.`;
  }
  if (h.status === 'testes') {
    if (r.avgjoer) return `${stor(eierF)} skriver funn og konklusjonen.`;
    if (r.vurderer) return `Ordlyden er låst. ${stor(eierF)} skriver funn og konklusjonen; ${eierF} eller ${beslF} kan sette på pause, og ${beslF} kan stoppe testen. Du kan korte fristen og skrive notat.`;
    if (r.eier) return `Funnene og konklusjonen skriver du på din side. Stopp og «Gi videre» er ${genitiv(navnAv(h.decider))}.`;
    return `${stor(navnAv(h.owner))} eier testen; ${navnAv(h.decider)} avgjør.`;
  }
  if (h.status === 'pa_pause') {
    if (r.avgjoer) return `${stor(eierF)} skriver funn og konklusjonen.`;
    if (r.vurderer) return `${stor(eierF)} eller ${beslF} avgjør om testen fortsetter; bare ${beslF} kan stoppe den.`;
    if (r.eier) return `Stopp og «Gi videre» er ${genitiv(navnAv(h.decider))}.`;
    return `${stor(navnAv(h.owner))} eier testen; ${navnAv(h.decider)} avgjør.`;
  }
  return null;
}

/** «2.–30. nov» i samme måned, ellers «28. okt–30. nov». */
export function periodeTekst(fra: string | undefined, til: string | undefined): string | null {
  if (!fra || !til) return null;
  const [a, b] = [kortDato(fra), kortDato(til)];
  const [dagA, mndA] = a.split(' ');
  const [, mndB] = b.split(' ');
  return mndA === mndB ? `${dagA}–${b}` : `${a}–${b}`;
}

/**
 * Testperioden slik den ble (H2.5-funn 5, DD-77). Sluttet testen før planlagt slutt — konkludert,
 * stoppet eller satt på pause — står den faktiske perioden og den planlagte sluttdatoen:
 * «27.–28. okt · planlagt til 31. des». Ellers den planlagte perioden. Dagen leses i Europe/Oslo.
 */
export function testetPeriode(h: Hypothesis): string | undefined {
  const { testStartsOn: fra, testEndsOn: til } = h;
  if (!fra || !til) return undefined;
  const slutt = h.status === 'testes' ? undefined : [...h.transitions].reverse().find((t) => t.kind === 'overgang' && t.fromStatus === 'testes');
  const sluttDag = slutt ? isoDag(new Date(slutt.at)) : undefined;
  if (!sluttDag || sluttDag >= til) return periodeTekst(fra, til) ?? undefined;
  const faktisk = sluttDag <= fra ? kortDato(fra) : periodeTekst(fra, sluttDag);
  return `${faktisk} · planlagt til ${kortDato(til)}`;
}

/** Den dagen datoen i løftet gjelder: fristen, parkeringen eller konklusjonen. */
export function loefteDato(h: HypothesisSummary): string | undefined {
  if (h.status === 'parkert' || h.status === 'pa_pause') return h.revisitAt ? kortDato(h.revisitAt) : undefined;
  if (h.status === 'testes') return h.conclusionDueAt ? kortDatoAv(h.conclusionDueAt) : undefined;
  const t = h.deadlineAt ?? h.projectedDeadlineAt;
  return t ? kortDatoAv(t) : undefined;
}

/**
 * Løftet innsenderen har fått, ORDRETT (DD-08): leietakerens mal med plassholderne fylt ut.
 * En plassholder uten verdi ennå vises som en tankestrek («Hypotesekontrakten før H1.4»).
 */
export function loefteFor(config: HypothesisConfig | undefined, h: HypothesisSummary): string | null {
  const noekkel = hypoteseStatusNoekkel(h.status, h.outcome);
  const ord = config?.statusWording.find((w) => w.key === noekkel);
  if (!ord) return null;
  const hel = h as Partial<Hypothesis>;
  const periode = hel.testStartsOn && hel.testEndsOn ? `${kortDato(hel.testStartsOn)}–${kortDato(hel.testEndsOn)}` : undefined;
  const verdier: Record<string, string | undefined> = {
    nummer: h.number !== undefined ? `H-${h.number}` : undefined,
    dato: loefteDato(h),
    vurderer: h.reviewer ? navnAv(h.reviewer) : undefined,
    eier: h.owner ? navnAv(h.owner) : undefined,
    beslutningstaker: h.decider ? navnAv(h.decider) : undefined,
    periode,
  };
  return fyllMal(ord.promise, verdier);
}

/** Løftet for en status hypotesen ikke står i ennå — det innsenderen FÅR (fotens setning). */
export function loefteVed(config: HypothesisConfig | undefined, h: HypothesisSummary, over: Partial<HypothesisSummary>): string | null {
  return loefteFor(config, { ...h, ...over });
}

/** Kvalitetssjekkens tre vurderinger i ord, for loggen og «Til deg» (aldri summert, aldri «2 av 3»). */
export function kvalitetssjekkTekst(t: HypothesisTransition): string | null {
  const k = t.reviewCheck;
  if (!k) return null;
  const kilde = { ja: 'ja, finnes', trenger_avklaring: 'trenger avklaring', maa_bygges: 'må bygges' }[k.sourceCheck];
  const base = { ja: 'ja, målt', maa_maales: 'må måles' }[k.baselineCheck];
  return `Kan bomme: ${k.canFail === 'ja' ? 'ja' : 'nei'} · Kilde: ${kilde} · Baseline: ${base}`;
}

/** Endelig: konkludert, eller avvist, stoppet og trukket — gruppen «Avsluttet» i «Mine hypoteser» (W7). */
export const erAvsluttet = (h: Pick<HypothesisSummary, 'statusType'>): boolean => h.statusType === 'complete' || h.statusType === 'closed';

/**
 * Dine innsendte i «Mine hypoteser»s rekkefølge (W7): «Sendt» før «Avsluttet», tjenerens rekkefølge
 * inni hver. Utkastene er ikke med — de har ikke nummer. Lista og j/k i hypotesen bruker samme rekke.
 */
export function mineIRekkefolge<T extends Pick<HypothesisSummary, 'status' | 'statusType'>>(alle: readonly T[]): T[] {
  const sendt = alle.filter((h) => h.status !== 'utkast' && !erAvsluttet(h));
  return [...sendt, ...alle.filter(erAvsluttet)];
}
